// The review page's checklist (UI-2): every item computed from what the API
// answered, none of it from what the page assumes. A blocking item that is not
// "ok" — failed, or not known yet — keeps the send button disabled, and the
// button says which one. The backend keeps its own gates (4.3: the approval
// condition in the `review → scheduled` transition); these are the screen's
// reading of the same facts, stated before anybody presses anything.

import type {
  CampaignGroupDetail,
  CampaignGroupSummary,
  Localization,
  SendingIdentity,
  TemplatePreview,
} from "@/lib/api";
import type { EstimateData, HaltData } from "@/pages/campaigns/data";
import {
  draftStarts,
  hourOf,
  insideWindow,
  isLocal,
  localMomentFor,
  nowIn,
  startsLine,
  startsText,
  windowText,
} from "@/pages/campaigns/model";
import { atViewer } from "@/components/emc/time";
import type { StepKey, TimingDraft } from "@/pages/campaigns/model";
import { labelOf } from "@/components/emc/status";

export type CheckStatus = "ok" | "fail" | "pending" | "warn";

export interface Check {
  key: string;
  /** The thing checked, as a statement that is true when it passes. */
  label: string;
  status: CheckStatus;
  /** What exactly passed, or what is wrong and how to put it right. */
  detail: string;
  /** A blocking check that is not ok keeps the send button disabled. */
  blocking: boolean;
  /** Where it is fixed. */
  step?: StepKey;
}

export type RenderState =
  | { status: "loading" }
  | { status: "ok"; preview: TemplatePreview; versionId: number }
  | { status: "error"; message: string };

export interface CheckInput {
  detail: CampaignGroupDetail;
  summary: CampaignGroupSummary;
  estimate: EstimateData;
  halt: HaltData;
  renders: Record<string, RenderState>;
  localizations: Localization[] | null;
  timing: TimingDraft;
  identityOf: (country: string) => SendingIdentity | null;
}

const list = (items: string[]) => items.join(", ");

function count(value: number): string {
  return value.toLocaleString("hu-HU");
}

/** The countries a send goes to: the ones not already stopped. */
export function sendingCountries(summary: CampaignGroupSummary): string[] {
  return summary.countries
    .filter((row) => row.state !== "cancelled" && row.state !== "failed")
    .map((row) => row.country);
}

/** The handover item, or null when no target country is migrating.
 *
 * N = 0 blocks: that country would assemble nobody, and the reason is not one
 * the campaign can fix — somebody has to raise N on the warmup page first.
 * Contacts left to Klaviyo elsewhere are a warning: the send is right to skip
 * them, and the marketer should see how many before pressing the button. */
export function handoverCheck(rows: EstimateData["rows"] & object): Check | null {
  const migrating = rows.filter((row) => row.handover_wave != null);
  if (migrating.length === 0) return null;
  const label = "Senki sem kap levelet a Klaviyóból is";
  const closed = migrating.filter((row) => row.handover_wave === 0).map((row) => row.country);
  if (closed.length > 0) {
    return {
      key: "handover",
      label,
      status: "fail",
      detail: `A Klaviyo-átadás N = 0-n áll: ${list(closed)}. Amíg valaki nem emeli N-et (Warmup → Klaviyo-átadás), ott senkinek nem küldhetünk.`,
      blocking: true,
      step: "cimzettek",
    };
  }
  const left = migrating.filter((row) => (row.handover ?? 0) > 0);
  const said = migrating
    .map((row) => `${row.country}: N = ${row.handover_wave}, ${count(row.handover ?? 0)} a Klaviyóé`)
    .join(" · ");
  return {
    key: "handover",
    label,
    status: left.length > 0 ? "warn" : "ok",
    detail:
      left.length > 0
        ? `Akiknek a Klaviyo még küld (hullám > N vagy nincs szinkronizálva), kimaradnak — ${said}.`
        : `Az átadás alatt álló országokban mindenki a mienk a közönségből — ${said}.`,
    blocking: false,
    step: "cimzettek",
  };
}

/** The Klaviyo sync item (6.2), or null when no target country has a
 * Klaviyo account to be out of date with.
 *
 * A warning, not a block: the snapshot still excludes everything we know, and
 * a sync that stopped is something to look at, not a reason the marketer can
 * fix from this page. Over an hour (or never) is four missed passes. */
/** The countries where Klaviyo mails everybody too (0045), or null when none.
 *
 * Not a failure: the owner chose it (2026-10-23). But the item above is about
 * "nobody gets both letters", which is not true there, and the marketer
 * should read that before pressing send. */
export function parallelCheck(rows: EstimateData["rows"] & object): Check | null {
  const parallel = rows.filter((row) => row.handover_parallel === true).map((row) => row.country);
  if (parallel.length === 0) return null;
  return {
    key: "handover_parallel",
    label: "A Klaviyo is küld nekik",
    status: "warn",
    detail: `${list(parallel)}: a Klaviyo is küld nekik — nincs átadás-szűrés. Ugyanaz a kontakt a Klaviyótól is kaphat levelet; minden más kizárás él.`,
    blocking: false,
    step: "cimzettek",
  };
}

export function klaviyoSyncCheck(rows: EstimateData["rows"] & object): Check | null {
  // A country the sync applies to is stale or has a time; the API sends
  // neither for a country with no Klaviyo account.
  const watched = rows.filter(
    (row) => row.klaviyo_sync_stale === true || Boolean(row.klaviyo_suppressions_synced_at),
  );
  if (watched.length === 0) return null;
  const stale = watched.filter((row) => row.klaviyo_sync_stale === true);
  const label = "A Klaviyo-leiratkozások egy órán belül frissültek";
  if (stale.length === 0) {
    return {
      key: "klaviyo-sync",
      label,
      status: "ok",
      detail: "Minden célországban egy órán belül érkezett szinkron a Klaviyóból.",
      blocking: false,
    };
  }
  return {
    key: "klaviyo-sync",
    label,
    status: "warn",
    detail:
      stale
        .map((row) =>
          // The time on the reader's clock: the API's own sentence says UTC.
          row.klaviyo_suppressions_synced_at
            ? `${row.country}: a Klaviyo-leiratkozások utoljára ${atViewer(row.klaviyo_suppressions_synced_at)}-kor érkeztek — egy óránál régebben.`
            : row.klaviyo_sync_message || `${row.country}: a Klaviyo-leiratkozások egy óránál régebbiek.`,
        )
        .join(" ") +
      " Amit a Klaviyo azóta kapott, azt a kizárás még nem ismeri — nézd meg a szinkront (emc doctor), mielőtt elküldöd.",
    blocking: false,
  };
}

export function reviewChecks(input: CheckInput): Check[] {
  const { detail, summary, estimate, halt, renders, timing, identityOf } = input;
  const countries = sendingCountries(summary);
  const checks: Check[] = [];

  // 1. Somewhere to send to.
  checks.push(
    countries.length === 0
      ? {
          key: "countries",
          label: "Van célország",
          status: "fail",
          detail: "Nincs célország. Válassz legalább egyet a Címzettek lépésben.",
          blocking: true,
          step: "cimzettek",
        }
      : {
          key: "countries",
          label: "Van célország",
          status: "ok",
          detail: `${countries.length} ország: ${list(countries)}`,
          blocking: true,
          step: "cimzettek",
        },
  );

  // 2. Somebody in every country, after the exclusions.
  const rows = (estimate.rows ?? []).filter((row) => countries.includes(row.country));
  if (estimate.rows === null) {
    checks.push({
      key: "recipients",
      label: "Minden országban van címzett a kizárások után",
      status: estimate.error !== null ? "fail" : "pending",
      detail:
        estimate.error !== null
          ? `A becslés nem készült el: ${estimate.error}. Kérd le újra a Címzettek lépésben.`
          : "A becslés készül — országonként végigszámolja a kontaktokat.",
      blocking: true,
      step: "cimzettek",
    });
  } else {
    const empty = rows.filter((row) => row.recipients <= 0).map((row) => row.country);
    const total = rows.reduce((sum, row) => sum + row.recipients, 0);
    checks.push(
      empty.length > 0
        ? {
            key: "recipients",
            label: "Minden országban van címzett a kizárások után",
            status: "fail",
            detail: `Nincs címzett: ${list(empty)}. Válassz más listát vagy szegmenst, vagy indíts nélküle új kampányt.`,
            blocking: true,
            step: "cimzettek",
          }
        : {
            key: "recipients",
            label: "Minden országban van címzett a kizárások után",
            status: "ok",
            detail: `${count(total)} címzett ${countries.length} országban`,
            blocking: true,
            step: "cimzettek",
          },
    );
  }

  // 2b. The Klaviyo handover (0028): nobody gets this letter and Klaviyo's.
  // Only where a country is migrating; a country nothing restricts says nothing.
  const handover = handoverCheck(rows);
  if (handover !== null) checks.push(handover);
  const parallel = parallelCheck(rows);
  if (parallel !== null) checks.push(parallel);

  // 2c. Klaviyo's unsubscribes reached us within the hour (6.2). Only rows
  // the API marked: a country with no Klaviyo account says nothing.
  const klaviyo = klaviyoSyncCheck(rows);
  if (klaviyo !== null) checks.push(klaviyo);

  // 3. A letter, and a published one: a draft version can still change under
  // a campaign that points at it.
  const letter = summary.letter;
  checks.push(
    letter === null
      ? {
          key: "letter",
          label: "Van levél",
          status: "fail",
          detail: "Nincs levél kiválasztva. Válassz egy publikált sablon-verziót a Tartalom lépésben.",
          blocking: true,
          step: "tartalom",
        }
      : letter.status !== "published"
        ? {
            key: "letter",
            label: "Van levél",
            status: "fail",
            detail: `A kiválasztott verzió (${letter.template_name} v${letter.version}) nem publikált. Publikáld a sablon-szerkesztőben, vagy válassz publikált verziót.`,
            blocking: true,
            step: "tartalom",
          }
        : {
            key: "letter",
            label: "Van levél",
            status: "ok",
            detail: `${letter.template_name} · v${letter.version}`,
            blocking: true,
            step: "tartalom",
          },
  );

  // 4. Every country's letter renders, with a subject — the way the sender
  // renders it (the preview picks the variant by the same function).
  if (letter !== null && countries.length > 0) {
    const states = countries.map((country) => [country, renders[country]] as const);
    const loading = states.filter(([, state]) => state === undefined || state.status === "loading");
    const failed = states.filter(
      (entry): entry is readonly [string, { status: "error"; message: string }] =>
        entry[1]?.status === "error",
    );
    const noSubject = states
      .filter(([, state]) => state?.status === "ok" && state.preview.subject.trim() === "")
      .map(([country]) => country);
    if (failed.length > 0) {
      checks.push({
        key: "render",
        label: "A levél minden országban elkészül, tárggyal",
        status: "fail",
        detail: failed.map(([country, state]) => `${country}: ${state.message}`).join(" · "),
        blocking: true,
        step: "tartalom",
      });
    } else if (loading.length > 0) {
      checks.push({
        key: "render",
        label: "A levél minden országban elkészül, tárggyal",
        status: "pending",
        detail: `Előnézet készül: ${list(loading.map(([country]) => country))}`,
        blocking: true,
        step: "tartalom",
      });
    } else if (noSubject.length > 0) {
      checks.push({
        key: "render",
        label: "A levél minden országban elkészül, tárggyal",
        status: "fail",
        detail: `Üres a tárgy: ${list(noSubject)}. A tárgy a sablon nyelvi változatában adható meg.`,
        blocking: true,
        step: "tartalom",
      });
    } else {
      checks.push({
        key: "render",
        label: "A levél minden országban elkészül, tárggyal",
        status: "ok",
        detail: `${countries.length} országban renderelve`,
        blocking: true,
        step: "tartalom",
      });
    }
  }

  // 5. The A/B arms, when there is a test.
  if (summary.ab !== null) {
    const arms = summary.ab_arms;
    const blank = arms.filter((arm) => arm.subject.trim() === "").map((arm) => arm.key);
    const share = arms.reduce((sum, arm) => sum + arm.share_permille, 0) / 10;
    const metric = typeof summary.ab.metric === "string" ? labelOf("abMetric", summary.ab.metric) : "—";
    const hours = typeof summary.ab.decide_after_hours === "number" ? summary.ab.decide_after_hours : null;
    checks.push(
      arms.length < 2 || blank.length > 0
        ? {
            key: "ab",
            label: "Minden A/B-variánsnak van tárgya",
            status: "fail",
            detail:
              arms.length < 2
                ? "Az A/B teszthez legalább két variáns kell. Állítsd be a Tartalom lépésben, vagy kapcsold ki a tesztet."
                : `Üres tárgy: ${list(blank)} variáns. Adj meg tárgyat a Tartalom lépésben.`,
            blocking: true,
            step: "tartalom",
          }
        : {
            key: "ab",
            label: "Minden A/B-variánsnak van tárgya",
            status: "ok",
            detail: `${arms.length} variáns a közönség ${share.toLocaleString("hu-HU")}%-án; döntés ${hours ?? "—"} óra után, ${metric} szerint`,
            blocking: true,
            step: "tartalom",
          },
    );
  }

  // 6. Localised mode: the approval gate, and what the approval is of.
  if (summary.mode === "localized") {
    const missing = detail.unapproved.filter((country) => countries.includes(country));
    checks.push(
      missing.length > 0
        ? {
            key: "approval",
            label: "Minden ország szövege jóváhagyva",
            status: "fail",
            detail: `Nincs jóváhagyva: ${list(missing)}. Jóváhagyás nélkül a kampány nem küldhető ki.`,
            blocking: true,
            step: "tartalom",
          }
        : {
            key: "approval",
            label: "Minden ország szövege jóváhagyva",
            status: "ok",
            detail: `Mind a(z) ${countries.length} ország jóváhagyva`,
            blocking: true,
            step: "tartalom",
          },
    );
    // An approval is of the source it was read against — the letter and the
    // source country's words. One given before the letter was switched, or
    // before the source was edited, holds the schedule until it is given again:
    // both halves of the backend gate refuse it, as they refuse a stale arm.
    const stale = summary.stale.filter((country) => countries.includes(country));
    checks.push(
      stale.length > 0
        ? {
            key: "stale",
            label: "A jóváhagyás a mostani forrásszövegre szól",
            status: "fail",
            detail: `A forrás a jóváhagyás óta változott (új levél vagy módosított forrásszöveg): ${list(stale)}. Olvasd át és hagyd jóvá újra — addig a kampány nem ütemezhető.`,
            blocking: true,
            step: "tartalom",
          }
        : {
            key: "stale",
            label: "A jóváhagyás a mostani forrásszövegre szól",
            status: "ok",
            detail: "Minden jóváhagyás a mostani levélre és forrásszövegre szól",
            blocking: true,
            step: "tartalom",
          },
    );
    // There used to be a "the approved translation is what goes out" item
    // here, failing whenever a translation was a letter version of its own:
    // the send rendered the group's letter. 4.3's fix freezes each country's
    // approved letter and wording onto its campaign at the schedule, and the
    // previews above render exactly that, so the item has nothing left to say.

    // Localised A/B: every arm of every country approved, and not stale — the
    // gate the schedule itself holds (both halves of it), named cell by cell.
    if (summary.ab !== null) {
      const cells = summary.unapproved_arms.filter((cell) =>
        countries.includes(cell.split("/")[0] ?? ""),
      );
      checks.push(
        cells.length > 0
          ? {
              key: "arm-approval",
              label: "Minden ország minden variánsa jóváhagyva",
              status: "fail",
              detail: `Nincs jóváhagyva, vagy a forrás azóta változott: ${list(cells)}. Amíg bármelyik hiányzik, a kampány nem ütemezhető.`,
              blocking: true,
              step: "tartalom",
            }
          : {
              key: "arm-approval",
              label: "Minden ország minden variánsa jóváhagyva",
              status: "ok",
              detail: `${countries.length} ország × ${summary.ab_arms.length} variáns jóváhagyva`,
              blocking: true,
              step: "tartalom",
            },
      );
    }
  }

  // 6b. Every country's sending account is fit to send from (0.1): the verdict
  // the campaign runner refuses a start on, read from the summary, one item per
  // country. Blocking only where the gate refuses (`enforced`); in a rehearsal
  // — local/test/dev on the null transport — the runner lets it through and so
  // does the screen, with a warning that says what production would do. Not
  // fixed by a step here — the values arrive by migration — so no "Javítás"
  // link; the Országbeállítások page has the whole account.
  for (const row of summary.countries.filter((entry) => countries.includes(entry.country))) {
    const key = `account-${row.country}`;
    const label = `${row.country}: a küldő-fiók kész`;
    const verdict = row.account_readiness;
    if (verdict === undefined || verdict === null) {
      checks.push({
        key,
        label,
        status: "fail",
        detail: "A fiók állapota nem érkezett meg az API-tól. Frissítsd az oldalt.",
        blocking: true,
      });
      continue;
    }
    if (!verdict.ready) {
      const problems = verdict.findings
        .filter((finding) => finding.status === "fail")
        .map((finding) => finding.message)
        .join(" ");
      // An answer without the flag (an older API) blocks: unknown is not "fine".
      checks.push(
        verdict.enforced !== false
          ? {
              key,
              label,
              status: "fail",
              detail: `${problems} Az Országbeállítások oldalon pótolható, ott látszik az egész fiók.`,
              blocking: true,
            }
          : {
              key,
              label,
              status: "warn",
              detail: `Élesben ez megállítaná a kiküldést: ${problems} Ez a környezet nem küld valódi levelet (helyi próba, null transport), ezért itt nem akadály.`,
              blocking: false,
            },
      );
      continue;
    }
    const identity = identityOf(row.country);
    checks.push({
      key,
      label,
      status: "ok",
      detail:
        identity === null
          ? "Feladó, cégadatok, követő domain rendben"
          : `${identity.sender_name} <${identity.sender_email}>`,
      blocking: true,
    });
    const warnings = verdict.findings.filter((finding) => finding.status === "warn");
    if (warnings.length > 0) {
      checks.push({
        key: `account-warn-${row.country}`,
        label: `${row.country}: a küldő-fiók`,
        status: "warn",
        detail: warnings.map((finding) => finding.message).join(" "),
        blocking: false,
      });
    }
  }

  // 7. The platform is not stopped (F1). Unknown is not "fine".
  if (halt.status === null) {
    checks.push({
      key: "halt",
      label: "A kiküldés nincs globálisan leállítva",
      status: halt.error !== null ? "fail" : "pending",
      detail:
        halt.error !== null
          ? `A leállítás állapota nem olvasható: ${halt.error}. Frissítsd az oldalt.`
          : "Olvasás…",
      blocking: true,
    });
  } else if (halt.status.halted) {
    checks.push({
      key: "halt",
      label: "A kiküldés nincs globálisan leállítva",
      status: "fail",
      detail: `A kiküldés áll — ${halt.status.halted_by ?? "ismeretlen"} állította le${halt.status.note ? `: ${halt.status.note}` : ""}. A feloldásig nem indítható.`,
      blocking: true,
    });
  } else {
    checks.push({
      key: "halt",
      label: "A kiküldés nincs globálisan leállítva",
      status: "ok",
      detail: "Nincs leállítás",
      blocking: true,
    });
  }

  // 8. When — a time in the past would send at once, which is not what
  // somebody who typed a date meant.
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";
  if (timing.mode === "now") {
    checks.push({
      key: "timing",
      label: "Az időzítés megvan",
      status: "ok",
      detail: "Most — minden ország a saját küldési ablakán belül indul",
      blocking: true,
      step: "idozites",
    });
  } else if (!isLocal(`${timing.date}T${timing.time}`)) {
    checks.push({
      key: "timing",
      label: "Az időzítés megvan",
      status: "fail",
      detail: "Adj meg dátumot és időpontot, vagy válaszd a »Most« lehetőséget.",
      blocking: true,
      step: "idozites",
    });
  } else {
    const past = countries.filter((country) => {
      const moment = localMomentFor(timing, country, zoneOf(country));
      return moment <= nowIn(zoneOf(country));
    });
    checks.push(
      past.length > 0
        ? {
            key: "timing",
            label: "Az időzítés megvan",
            status: "fail",
            detail: `Az időpont helyi idő szerint már elmúlt: ${list(past)}. Válassz későbbi időpontot, vagy a »Most« lehetőséget.`,
            blocking: true,
            step: "idozites",
          }
        : {
            key: "timing",
            label: "Az időzítés megvan",
            status: "ok",
            detail: startsText(startsLine(draftStarts(timing, countries, zoneOf), zoneOf)),
            blocking: true,
            step: "idozites",
          },
    );
  }

  // 9. The sending window: outside it the letters wait for it to open.
  const outside = countries.flatMap((country) => {
    const identity = identityOf(country);
    if (identity === null) return [];
    const hour = hourOf(localMomentFor(timing, country, identity.timezone));
    if (hour === null) return [];
    return insideWindow(hour, identity.send_window_start_hour, identity.send_window_end_hour)
      ? []
      : [`${country} (${windowText(identity, localMomentFor(timing, country, identity.timezone).slice(0, 10))})`];
  });
  if (outside.length > 0) {
    checks.push({
      key: "window",
      label: "Az indulás a küldési ablakba esik",
      status: "warn",
      detail: `Ablakon kívül: ${list(outside)}. Ezekben az országokban a levelek az ablak nyitásakor indulnak.`,
      blocking: false,
      step: "idozites",
    });
  }

  // 10. Today's warmup room, per pool. Not a gate: the limit is the code's
  // (rule 4), and what does not fit today goes the next days.
  const tight = rows.filter((row) => row.warning !== "");
  if (tight.length > 0) {
    checks.push({
      key: "budget",
      label: "A mai warmup-keretbe belefér",
      status: "warn",
      detail: `${tight.map((row) => `${row.country}: ${row.warning}`).join(" ")} A keretet a kód tartja; ami ma nem fér bele, a következő napokon megy ki.`,
      blocking: false,
      step: "cimzettek",
    });
  }

  return checks;
}

/** The first thing that keeps the send button disabled, or null. */
export function firstBlocker(checks: Check[]): Check | null {
  return checks.find((check) => check.blocking && check.status !== "ok") ?? null;
}
